import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

const SQ_CART_CLOSE_FALLBACK_MS = 460;
const SQ_CART_SINGLE_PATH_SCRIPT = new URL('sq-cart-single-path-v10.js', import.meta.url).href;
const SQ_CART_SINGLE_PATH_STYLE = new URL('sq-cart-single-path-v10.css', import.meta.url).href;

const loadSereinCartSinglePath = () => {
  if (!document.querySelector('link[data-sq-cart-single-path]')) {
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.href = SQ_CART_SINGLE_PATH_STYLE;
    style.dataset.sqCartSinglePath = 'true';
    document.head.appendChild(style);
  }

  if (!window.__SQ_CART_SINGLE_PATH_LOADING__) {
    window.__SQ_CART_SINGLE_PATH_LOADING__ = import(SQ_CART_SINGLE_PATH_SCRIPT).catch((error) => {
      console.warn('[Serein Queen cart single path]', error);
      return null;
    });
  }
};

const lockedScrollPosition = () => {
  const bodyTop = Number.parseFloat(document.body.style.top || '');
  return Number.isFinite(bodyTop) ? Math.abs(bodyTop) : window.scrollY;
};

const wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const shouldSuppressCartAutoOpen = () =>
  Date.now() < Number(window.__SQ_SUPPRESS_CART_OPEN_UNTIL__ || 0);

loadSereinCartSinglePath();

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {Promise<void> | null} */
  #closingPromise = null;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('sq-cart-primary');
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    void window.__SQ_CART_SINGLE_PATH_LOADING__;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.close();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !shouldSuppressCartAutoOpen()) {
      this.open();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.classList.add('sq-cart-primary');
    document.documentElement.classList.add('sq-single-cart-active');
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.close, { once: true });
    });
  }

  #forceClose(dialog, scrollY) {
    try {
      if (dialog.open) dialog.close();
    } catch (_) {}

    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';
    dialog.style.removeProperty('--dialog-drawer-closing-animation');

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.classList.remove('sq-single-cart-active');

    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    });
  }

  close = async () => {
    const dialog = this.refs.dialog;
    if (!dialog?.open) {
      document.documentElement.classList.remove('sq-single-cart-active');
      return;
    }

    if (this.#closingPromise) return this.#closingPromise;

    const scrollY = lockedScrollPosition();
    const nativeClose = Promise.resolve(this.closeDialog()).catch(() => undefined);

    this.#closingPromise = (async () => {
      await Promise.race([nativeClose, wait(SQ_CART_CLOSE_FALLBACK_MS)]);
      if (dialog.open) this.#forceClose(dialog, scrollY);
      document.documentElement.classList.remove('sq-single-cart-active');
      this.#closingPromise = null;
    })();

    return this.#closingPromise;
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
